import { ReactNode } from 'react';

import { Icon, IconName } from '~/modules/elements/akui/icon';
import InfoGlyph from '~/modules/elements/info-glyph';
import { useRegister } from '~/modules/hooks/use-keyboard-nav';
import { ControlInput } from '~/routes/keyboard-help/controls';
import { PanelLabel } from '~/routes/profiles/profile-panels';
import { domNavProps } from '~/routes/singers/nav-props';

interface NavProps {
  focused?: boolean;
  [key: string]: unknown;
}

export const SettingsCard = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <div
    className={`grid [grid-template-columns:minmax(0,1fr)] gap-[1.625rem] rounded-xl border border-white/10 bg-black/45 p-[1.625rem] ${className}`}>
    {children}
  </div>
);

export const SettingsSection = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-2.5">
    <PanelLabel>{label}</PanelLabel>
    {children}
  </div>
);

interface RowProps {
  navProps: NavProps;
  label: string;
  value: ReactNode;
  info?: string;
  /** `register` already supplies `data-test`; only the value needs adding on top of it. */
  'data-test-value'?: string;
}

/**
 * The `Switcher` shape - label left, accent value right - at the density Settings needs. Its own
 * element rather than `Switcher` itself because these rows sit on the raised slate surface inside
 * the card, where `Switcher`'s `shadow-focusable` button chrome reads as a second card.
 */
export const SettingsRow = ({ navProps, label, value, info, ...rest }: RowProps) => (
  <button
    type="button"
    {...domNavProps(navProps)}
    {...rest}
    className={`flex h-[3.625rem] cursor-pointer items-center justify-between gap-2.5 rounded-lg bg-slate-800 px-[1.125rem] ${
      navProps.focused ? 'shadow-[inset_0_0_0_2px_#facc15]' : 'shadow-[inset_0_0_0_1px_rgba(255,255,255,.08)]'
    }`}>
    <span className="flex min-w-0 items-center gap-2 text-[1.25rem] text-slate-100">
      <span className="truncate">{label}</span>
      {info && <InfoGlyph text={info} />}
    </span>
    <span className="text-active shrink-0 text-[1.25rem] font-bold">{value}</span>
  </button>
);

interface NavRowProps extends Omit<RowProps, 'navProps'> {
  name: string;
  onClick: () => void;
  /** Label for the on-screen keyboard hint, which is not always the row's own label. */
  help?: string;
  isDefault?: boolean;
  control: ControlInput;
}

/**
 * A settings row that registers itself, taking `register` from the nearest `KeyboardNavContext`.
 *
 * Its own component because `register` called in the parent's body runs before any child renders,
 * which puts the row at the FRONT of the navigation list - and of the list mirrored to the phone -
 * rather than where it is drawn. `in-game-audio-settings.tsx` makes a component of every row it owns
 * for the same reason, and says so.
 */
export const NavSettingsRow = ({ name, onClick, help, isDefault = false, control, ...rest }: NavRowProps) => {
  const register = useRegister();

  return <SettingsRow navProps={register(name, onClick, help, isDefault, { control })} {...rest} />;
};

interface TileProps {
  navProps: NavProps;
  label: string;
  note?: string;
  icon: IconName;
  /** Replaces `icon` when a destination has a glyph of its own, e.g. the microphone path. */
  glyph?: ReactNode;
}

/** A place you go, not a setting you change - the split that is the substance of this redesign. */
export const DestinationTile = ({ navProps, label, note, icon, glyph }: TileProps) => (
  <button
    type="button"
    {...domNavProps(navProps)}
    className={`shadow-focusable flex h-[4.75rem] cursor-pointer items-center gap-3.5 rounded-lg bg-black/55 px-[1.125rem] ${
      navProps.focused ? 'subtle-focus' : ''
    }`}>
    {glyph ?? <Icon icon={icon} className="text-active shrink-0" width="1.625rem" height="1.625rem" />}
    <span className="flex min-w-0 flex-1 flex-col text-left">
      <span className="truncate text-[1.25rem] font-bold uppercase">{label}</span>
      {note && <span className="text-active text-[0.9375rem]">{note}</span>}
    </span>
    <Icon icon="ic:baseline-arrow-forward" className="text-active shrink-0" width="1.625rem" height="1.625rem" />
  </button>
);
